import type { Manifest } from "@procore/bim-index";

import { REQUIRED_SECTIONS } from "features/bimIndex/availability";
import { bimIndexInfo } from "features/bimIndex/session";

// Names below mirror `@novorender/api` `offline/path.ts`, which keeps them internal.
const STATE = "state.json";
const JOURNAL = "journal";
const DELETE_QUEUE = "delete_queue";
const COMPLETION_MARKER = "index.json";
const LAST_ACCESS = "last_access_timestamp";
const BIM_INDEX = "bim_index";
const HOST_DATA = "host_data";
const MINIMAP = "minimap";
const PARTIAL_SUFFIX = ".partial";
const RESERVED_FILES = new Set([
    STATE,
    JOURNAL,
    `${JOURNAL}${PARTIAL_SUFFIX}`,
    DELETE_QUEUE,
    COMPLETION_MARKER,
    LAST_ACCESS,
]);

/** Paths beyond this are counted but not listed. */
const MAX_LISTED_PATHS = 50;

type Failed = { error: string };

/** A file's size on disk; reading it fails while the worker holds the file open. */
type FileSizes = Map<string, number | Failed>;

/** What `readSyncState` (`sync/record.ts`) reads a scene directory as. */
type SyncReading = "full" | "incremental" | "empty" | "unreadable";

/** Everything stored for one offline scene in OPFS, read without changing or locking anything. */
export async function collectSceneStorage(dirName: string) {
    const opfsPath = `/${dirName}`;
    const root = await navigator.storage.getDirectory();
    let sceneDir: FileSystemDirectoryHandle;
    try {
        sceneDir = await root.getDirectoryHandle(dirName);
    } catch (e) {
        if (e instanceof DOMException && e.name === "NotFoundError") {
            return { opfsPath, exists: false, locks: await settle(() => sceneLocks(dirName)) };
        }
        throw e;
    }

    const onDisk: FileSizes = new Map(await listFiles(sceneDir));
    const files = [...onDisk.keys()];
    const stateText = await settle(() => readText(sceneDir, STATE));
    const markerText = await settle(() => readText(sceneDir, COMPLETION_MARKER));
    const sync =
        isFailed(stateText) || isFailed(markerText)
            ? undefined
            : readSyncState(stateText, markerText, onDisk.has(JOURNAL));
    const committed = sync?.generation;
    const journal = await settle(async () => parseJournal(await readBytes(sceneDir, JOURNAL)));
    const journaled = isFailed(journal) ? undefined : journal.entries;

    return {
        opfsPath,
        exists: true,
        entries: countByTopLevel(files),
        partialFiles: listed(files.filter((path) => path.endsWith(PARTIAL_SUFFIX))),
        state: isFailed(stateText) ? stateText : parsedOrText(stateText),
        completionMarker: isFailed(markerText) ? markerText : parsedOrText(markerText),
        syncReading: sync?.reading ?? null,
        lastAccess: await settle(() => readLastAccess(sceneDir)),
        deleteQueue: await settle(() => readDeleteQueue(sceneDir)),
        bimIndex: {
            committedGeneration: committed,
            generations: generationsOf(BIM_INDEX, files, committed, { legacyLayout: true }),
            currentGeneration:
                committed === undefined ? null : await currentGeneration(sceneDir, committed, onDisk, journaled),
        },
        hostDataGenerations: generationsOf(HOST_DATA, files, committed, { legacyLayout: false }),
        minimap: {
            filesOnDisk: files.filter((path) => path.startsWith(`${MINIMAP}/`)).length,
            journaledBytes: journaled && sumBytes(journaled, (path) => path.startsWith(`${MINIMAP}/`)),
        },
        journal: isFailed(journal) ? journal : summarizeJournal(journal, onDisk),
        openIndex: bimIndexInfo(),
        locks: await settle(() => sceneLocks(dirName)),
        quota: await settle(async () => ({
            ...(await navigator.storage.estimate()),
            persisted: await navigator.storage.persisted(),
        })),
    };
}

function isFailed(value: unknown): value is Failed {
    return typeof value === "object" && value !== null && "error" in value;
}

async function settle<T>(read: () => Promise<T>): Promise<T | Failed> {
    try {
        return await read();
    } catch (e) {
        return { error: e instanceof Error ? `${e.name}: ${e.message}` : String(e) };
    }
}

function listed<T>(paths: T[]) {
    return { count: paths.length, paths: paths.slice(0, MAX_LISTED_PATHS) };
}

function isRecordObject(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Mirrors `isSyncIdShaped` in `@novorender/api` `offline/path.ts`. */
function isSyncIdShaped(name: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(name);
}

/** Every file under `dir` by its `/`-separated path, with its size. */
async function listFiles(dir: FileSystemDirectoryHandle, prefix = ""): Promise<[string, number | Failed][]> {
    const files: Promise<[string, number | Failed][]>[] = [];
    for await (const [name, handle] of dir.entries()) {
        const path = `${prefix}${name}`;
        files.push(
            handle.kind === "file"
                ? settle(async () => (await (handle as FileSystemFileHandle).getFile()).size).then((size) => [
                      [path, size],
                  ])
                : listFiles(handle as FileSystemDirectoryHandle, `${path}/`),
        );
    }
    return (await Promise.all(files)).flat();
}

/** File counts per top-level folder; top-level files under `"."`. */
function countByTopLevel(files: string[]) {
    const counts: Record<string, number> = {};
    for (const path of files) {
        const slash = path.indexOf("/");
        const key = slash < 0 ? "." : path.slice(0, slash);
        counts[key] = (counts[key] ?? 0) + 1;
    }
    return { total: files.length, byFolder: counts };
}

async function fileIfExists(dir: FileSystemDirectoryHandle, path: string): Promise<File | undefined> {
    const segments = path.split("/");
    try {
        let folder = dir;
        for (const segment of segments.slice(0, -1)) {
            folder = await folder.getDirectoryHandle(segment);
        }
        return await (await folder.getFileHandle(segments[segments.length - 1])).getFile();
    } catch (e) {
        if (e instanceof DOMException && e.name === "NotFoundError") {
            return undefined;
        }
        throw e;
    }
}

async function readBytes(dir: FileSystemDirectoryHandle, path: string): Promise<Uint8Array | null> {
    const file = await fileIfExists(dir, path);
    return file ? new Uint8Array(await file.arrayBuffer()) : null;
}

async function readText(dir: FileSystemDirectoryHandle, path: string): Promise<string | undefined> {
    return (await fileIfExists(dir, path))?.text();
}

async function readJson(dir: FileSystemDirectoryHandle, path: string): Promise<unknown> {
    const text = await readText(dir, path);
    return text === undefined ? null : JSON.parse(text);
}

function tryParseJson(text: string): unknown {
    try {
        return JSON.parse(text);
    } catch {
        return undefined;
    }
}

/** A JSON file's value, its text if it doesn't parse, `null` if it doesn't exist. */
function parsedOrText(text: string | undefined): unknown {
    return text === undefined ? null : (tryParseJson(text) ?? text);
}

async function readLastAccess(dir: FileSystemDirectoryHandle) {
    return (await (await fileIfExists(dir, LAST_ACCESS))?.text()) ?? null;
}

async function readDeleteQueue(dir: FileSystemDirectoryHandle) {
    const text = await (await fileIfExists(dir, DELETE_QUEUE))?.text();
    return text === undefined ? null : listed(text.split(",").filter(Boolean));
}

/**
 * How `readSyncState` (`sync/record.ts`) reads the directory, and the generation `openBimIndexLease` opens.
 * @param state `state.json`'s text; `undefined` if it doesn't exist.
 * @param marker `index.json`'s text; `undefined` if it doesn't exist.
 * @param hasJournal Whether `journal` exists.
 * @returns `generation`: `null` for the legacy layout, which keeps the index directly in `bim_index/`;
 * `undefined` for none.
 */
export function readSyncState(
    state: string | undefined,
    marker: string | undefined,
    hasJournal: boolean,
): { reading: SyncReading; generation?: string | null } {
    if (state !== undefined) {
        const record = tryParseJson(state);
        if (!isRecordObject(record) || record.version !== 1) {
            return { reading: "unreadable" };
        }
        if (record.mode === "incremental") {
            return { reading: "incremental" };
        }
        if (record.mode !== "full" || !isFullSyncRecord(record)) {
            return { reading: "unreadable" };
        }
        const { completedSyncId } = record;
        return {
            reading: "full",
            generation:
                completedSyncId === undefined ? null : isSyncIdShaped(completedSyncId) ? completedSyncId : undefined,
        };
    }
    // Without a record, a completion marker is a completed full sync in the legacy layout.
    if (marker !== undefined) {
        const index = tryParseJson(marker);
        const manifest = isRecordObject(index) && isRecordObject(index.offline) ? index.offline.manifest : undefined;
        return typeof manifest === "string" && manifest.length > 0 && !manifest.includes("/")
            ? { reading: "full", generation: null }
            : { reading: "unreadable" };
    }
    return { reading: hasJournal ? "incremental" : "empty" };
}

/** Mirrors `parseFullSyncStateRecord` in `sync/record.ts`, down to the fields that make a record unreadable. */
function isFullSyncRecord(record: Record<string, unknown>): record is { completedSyncId?: string } {
    const optional = (value: unknown, type: "number" | "string") => value === undefined || typeof value === type;
    const optionalTotals = (value: unknown) =>
        value === undefined ||
        (isRecordObject(value) && typeof value.bytes === "number" && typeof value.files === "number");
    const { phase } = record;
    return (
        (phase === "syncing" || phase === "paused" || phase === "synced") &&
        typeof record.targetManifestId === "string" &&
        optionalTotals(record.targetTotals) &&
        optional(record.startedAt, "number") &&
        optional(record.completedManifestId, "string") &&
        optional(record.completedAt, "number") &&
        optionalTotals(record.completedTotals) &&
        optionalTotals(record.downloaded) &&
        optional(record.completedSyncId, "string") &&
        (phase !== "synced" || (record.completedManifestId !== undefined && record.completedTotals !== undefined))
    );
}

/**
 * The `<folder>/<generation>/` folders on disk, and what the sweep (`sync/generations.ts`) would remove
 * once a sync commits `committed`.
 * @param committed The committed generation: `null` for the legacy layout, `undefined` for none, when
 * nothing is orphaned yet.
 * @param options.legacyLayout Whether files outside a sync-id-shaped folder are the legacy layout's index,
 * kept while `committed` is `null`. Otherwise every subfolder is a generation.
 */
function generationsOf(
    folder: string,
    files: string[],
    committed: string | null | undefined,
    { legacyLayout }: { legacyLayout: boolean },
) {
    const counts = new Map<string, number>();
    let otherFiles = 0;
    for (const path of files) {
        const [top, first, ...rest] = path.split("/");
        if (top !== folder) {
            continue;
        }
        if (rest.length > 0 && (!legacyLayout || isSyncIdShaped(first))) {
            counts.set(first, (counts.get(first) ?? 0) + 1);
        } else {
            otherFiles++;
        }
    }
    const nothingCommitted = committed === undefined;
    return {
        generations: [...counts].map(([id, files]) => ({ id, files, committed: id === committed })),
        orphaned: nothingCommitted ? null : [...counts.keys()].filter((id) => id !== committed),
        /** Files outside a generation: the legacy layout's index in `bim_index/`. */
        otherFiles,
        otherFilesOrphaned: nothingCommitted ? null : otherFiles > 0 && !(legacyLayout && committed === null),
    };
}

async function currentGeneration(
    sceneDir: FileSystemDirectoryHandle,
    generation: string | null,
    onDisk: FileSizes,
    journaled: Map<string, number> | undefined,
) {
    const folder = generation === null ? BIM_INDEX : `${BIM_INDEX}/${generation}`;
    const inFolder = [...onDisk.keys()]
        .filter((path) => path.startsWith(`${folder}/`))
        .map((path) => path.slice(folder.length + 1))
        // The legacy layout's folder also holds the generations.
        .filter((name) => generation !== null || !(name.includes("/") && isSyncIdShaped(name.split("/")[0])));
    const manifest = await settle(() => readJson(sceneDir, `${folder}/manifest.json`) as Promise<Manifest | null>);
    const listedSizes: unknown = isFailed(manifest) || !isRecordObject(manifest) ? undefined : manifest.files;

    return {
        folder,
        files: inFolder.map((name) => ({
            name,
            bytes: onDisk.get(`${folder}/${name}`),
            journaledBytes: journaled?.get(`${folder}/${name}`) ?? null,
        })),
        missingRequired: REQUIRED_SECTIONS.filter((name) => !inFolder.includes(name)),
        manifest,
        manifestCheck:
            listedSizes === undefined
                ? null
                : isRecordObject(listedSizes)
                  ? checkManifest(folder, listedSizes, inFolder, onDisk, journaled)
                  : { error: "`files` is not an object" },
    };
}

function checkManifest(
    folder: string,
    listedSizes: Record<string, unknown>,
    inFolder: string[],
    onDisk: FileSizes,
    journaled: Map<string, number> | undefined,
) {
    return {
        missing: Object.keys(listedSizes).filter((name) => !inFolder.includes(name)),
        sizeMismatch: Object.entries(listedSizes).flatMap(([name, manifestBytes]) => {
            const bytes = onDisk.get(`${folder}/${name}`);
            const journaledBytes = journaled?.get(`${folder}/${name}`);
            return (typeof bytes === "number" && bytes !== manifestBytes) ||
                (journaledBytes !== undefined && journaledBytes !== manifestBytes)
                ? [{ name, manifestBytes, bytes: bytes ?? null, journaledBytes: journaledBytes ?? null }]
                : [];
        }),
        notInManifest: inFolder.filter((name) => name !== "manifest.json" && !(name in listedSizes)),
    };
}

/** `name,size` lines; a last line without its newline is an append in progress. */
function parseJournal(bytes: Uint8Array | null) {
    const lines = bytes ? new TextDecoder().decode(bytes).split("\n") : [];
    const unterminated = lines.pop() ?? "";
    // A rewritten file is appended again, so its last line is the current size.
    const entries = new Map<string, number>();
    const malformed: string[] = [];
    const duplicates = new Set<string>();
    for (const line of lines) {
        const comma = line.indexOf(",");
        const size = line.slice(comma + 1);
        if (comma <= 0 || !/^[0-9]+$/.test(size)) {
            malformed.push(line);
            continue;
        }
        const name = line.slice(0, comma);
        if (entries.has(name)) {
            duplicates.add(name);
        }
        entries.set(name, Number(size));
    }
    return {
        exists: bytes !== null,
        fileBytes: bytes?.length ?? 0,
        lines: lines.length,
        unterminated,
        entries,
        malformed,
        duplicates,
    };
}

function sumBytes(entries: Map<string, number>, include: (path: string) => boolean) {
    let total = 0;
    for (const [path, bytes] of entries) {
        if (include(path)) {
            total += bytes;
        }
    }
    return total;
}

function summarizeJournal(journal: ReturnType<typeof parseJournal>, onDisk: FileSizes) {
    const { entries } = journal;
    return {
        exists: journal.exists,
        fileBytes: journal.fileBytes,
        lines: journal.lines,
        files: entries.size,
        totalBytes: sumBytes(entries, () => true),
        malformed: listed(journal.malformed),
        duplicates: listed([...journal.duplicates]),
        unterminatedLine: journal.unterminated || null,
        journaledNotOnDisk: listed([...entries.keys()].filter((path) => !onDisk.has(path))),
        // A file counts only at its journaled size (`sync/journal.ts`).
        sizeMismatch: listed(
            [...entries].flatMap(([path, journaledBytes]) => {
                const bytes = onDisk.get(path);
                return typeof bytes === "number" && bytes !== journaledBytes ? [{ path, journaledBytes, bytes }] : [];
            }),
        ),
        sizeUnreadable: listed([...entries.keys()].filter((path) => isFailed(onDisk.get(path)))),
        onDiskNotJournaled: listed(
            [...onDisk.keys()].filter(
                (path) =>
                    !entries.has(path) &&
                    !RESERVED_FILES.has(path) &&
                    !path.endsWith(PARTIAL_SUFFIX) &&
                    // Host data is never journaled.
                    !path.startsWith(`${HOST_DATA}/`),
            ),
        ),
    };
}

/** Every offline lock name ends in `:<dirName>`, or continues with `/…` after it. */
async function sceneLocks(dirName: string) {
    const { held = [], pending = [] } = await navigator.locks.query();
    const ours = ({ name = "" }: LockInfo) =>
        name.startsWith("novorender-offline") && (name.endsWith(`:${dirName}`) || name.includes(`:${dirName}/`));
    const describe = ({ name, mode }: LockInfo) => ({ name, mode });
    return { held: held.filter(ours).map(describe), pending: pending.filter(ours).map(describe) };
}
